<template>
    <div class="parentContainer">
      <canvas id="design-canvas" />
      <Layer v-if="isStageLoaded"/>
    </div>
  </template>
  <script>
  import {
      loadStage,
      destroyStudio,
  } from "../../core/utilities/componentManager"
  import Layer from "./layer.vue";
  import {main} from '../../main';
  import { useStudioStore } from "../store/studioStore";
  import { useContactDataStore } from "../store/contactDataStore";
  import studioDataService from "../../services/studioDataService";
  import appMode from "../../services/appMode";
  import { postToRN } from "../../utils/rnBridge";
  
  let stage = {};
  
  export default {
      name: 'DesignCanvas',
      components: {
          Layer,
      },
  
      data() {
          return {
              isStageLoaded: false,
              referenceID: this.$route.params.referenceID,
              contactId: this.$route.params.contactId || null,
          };
      },
  
      props: {
          latitude: {
              type: Number,
              default: 28.612948,
          },
          longitude: {
              type: Number,
              default: 77.229557,
          },
          zoom: {
              type: Number,
              default: 19,
          },
          designSettings: {
              type: Object,
              default() {
                  return {};
              },
          },
          mapImage: {
              type: Object,
              default() {
                  return {};
              },
          },
          stageData: {
              type: Object,
              default() {
                  return {};
              },
          },
          editMode: {
              type: Boolean,
              default: false,
          },
          overviewMode: {
              type: Boolean,
              default: false,
          },
          designId: {
              type: Number,
              default: -1,
          },
      },
      mounted() {
          this.initializeStudio();
      },
      
      methods: {
          async initializeStudio() {
              // Detect mode from URL ?arkaApp=<contactId> param (persists via sessionStorage)
              appMode.initialize();

              const designCanvas = document.getElementById('design-canvas');
              const parent = designCanvas.parentElement;
              const parentPadding = parseInt(
                  window.getComputedStyle(parent, null).getPropertyValue('padding-left'),
                  10,
              );
              const headerEl = document.querySelector('.arka-topbar');
              const headerHeight = headerEl ? headerEl.offsetHeight : 0;
              designCanvas.width = window.innerWidth;
              designCanvas.height = window.innerHeight - headerHeight;

              const studioStore = useStudioStore();

              if (appMode.isAppMode()) {
                  // ── APP / API MODE ─────────────────────────────────────────────
                  // Opened via ?arkaApp=<contactId> (Android, iOS WebView, or test browser)
                  try {
                      const contactId = appMode.getContactId() || this.$route.params.contactId || null;
                      const designData = await studioDataService.initializeDesign(contactId);

                      // Populate contactDataStore — always, even for new contacts
                      const contactStore = useContactDataStore();
                      if (studioDataService.rawApiData) {
                          // Existing contact: use full API data
                          contactStore.populate(contactId, studioDataService.rawApiData);
                      } else {
                          // New contact (API returned 404): build minimal data from designData
                          // so App.js loadMapData() can find coordinates
                          contactStore.populate(contactId, {
                              input_details: {
                                  latitude: designData.mapData.latitude,
                                  longitude: designData.mapData.longitude,
                              },
                              design_details: null,
                              report_json: null,
                          });
                      }

                      const savedStageData = designData.stageData;
                      const mapImage = {
                          url: designData.mapData.imageUrl || '',
                          width: designData.mapData.imageDimensions?.width || 300,
                          height: designData.mapData.imageDimensions?.height || 300
                      };

                      stage = await loadStage(
                          designCanvas,
                          designData.mapData.latitude,
                          designData.mapData.longitude,
                          designData.mapData.zoom,
                          this.designSettings,
                          mapImage,
                          savedStageData,
                      );

                      main.app.config.globalProperties.$stageInstance = stage;
                      this.isStageLoaded = true;

                      // Restore saved store state without triggering side effects.
                      // SET_INFORMATION_FORM_DATA_STORE() forcibly resets to DRAWING_STATE
                      // and creates/updates polygons, which is wrong for loaded designs.
                      // Instead, just assign the data directly — layer.vue mounted() will
                      // read the state and handle initialization appropriately.
                      if (designData.infoFormData) {
                          studioStore.infoFormData = designData.infoFormData;
                      }
                      if (designData.currentAppState) {
                          studioStore.currentAppState = designData.currentAppState;
                      }

                      // Restore saved panel/inverter settings if present
                      const saved = designData.panelSettings;
                      if (saved) {
                          if (saved.panel)   studioStore.SET_SELECTED_PANEL(saved.panel);
                          if (saved.inverter) studioStore.SET_SELECTED_INVERTER(saved.inverter);
                          if (saved.inverterQuantity != null) studioStore.SET_INVERTER_QUANTITY(saved.inverterQuantity);
                          studioDataService.setPanelSettings(saved);
                      }

                      // Notify ArkaGo that the portal is ready for user interaction
                      postToRN('PORTAL_READY');

                  } catch (error) {
                      console.error('[STUDIO] App mode initialization failed:', error);
                      // Always set isStageLoaded so the UI is not stuck forever
                      this.isStageLoaded = true;
                      // Always notify RN so the loading overlay is dismissed even on error.
                      // Without this the overlay stays stuck forever on the mobile side.
                      postToRN('PORTAL_READY');
                      this.$message({
                          message: 'Failed to load design. Please try again.',
                          type: 'error',
                          center: true,
                      });
                  }

              } else {
                  // ── WEB / localStorage MODE ────────────────────────────────────
                  // Original behavior from dev branch — uses localStorage for stageData
                  let savedStageData = this.stageData;
                  if (localStorage.getItem('stageData')) {
                      savedStageData = JSON.parse(localStorage.getItem('stageData'));
                  } else {
                      localStorage.setItem('stageData', JSON.stringify(this.stageData));
                  }

                  stage = await loadStage(
                      designCanvas,
                      this.latitude,
                      this.longitude,
                      this.zoom,
                      this.designSettings,
                      this.mapImage,
                      savedStageData,
                  );

                  main.app.config.globalProperties.$stageInstance = stage;
                  this.isStageLoaded = true;

                  // Initialize store with defaults (original dev behavior)
                  studioStore.infoFormData = {
                      floorValue: 5,
                      supply_phase: 'Single Phase',
                      tariff_rate: 7,
                      monthly_bill: 2000,
                      sanction_load: 5,
                  };
                  studioStore.SET_INFORMATION_FORM_DATA_STORE();
              }

              if (!this.editMode) {
                  stage.setDesignId(this.designId);
              }
              if (this.overviewMode) {
                  stage.switchTo3d();
              }
          }
      },
      beforeDestroy() {
          stage.destroyStage();
          destroyStudio();
      },
  };
  </script>
  
  <style  scoped>
    .parentContainer {
      position: relative;
      height: 100%;
      width: 100%;
      overflow: hidden;
      background: rgb(222, 201, 201);
    }

    #design-canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
  </style>